import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { TilopayFetchAPI } from "../../api/Tilopay";
import {
  PAYMENT_PENDING_STATUSES,
  clearCheckoutStorageAfterPayment,
  getStatusCopy,
  writePending,
} from "./tilopayHelpers";

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_MS = 3 * 60 * 1000;
const REDIRECT_DELAY_MS = 1500;

/**
 * Destino del redirect de Tilopay (GET /api/tilopay/return -> /tilopay/result?order=...).
 * No confia en nada de la URL salvo el numero de orden: el estado se pregunta al servidor, que lo confirma
 * contra Tilopay. Una orden solo se considera pagada cuando el servidor responde "registered".
 */
const TilopayResult = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const order = params.get("order") || "";

  const [view, setView] = useState(null);
  const [problem, setProblem] = useState("");
  const [timedOut, setTimedOut] = useState(false);
  const [round, setRound] = useState(0);

  useEffect(() => {
    if (!order) return undefined;
    let cancelled = false;
    let timer;
    const startedAt = Date.now();
    setProblem("");
    setTimedOut(false);

    const tick = async () => {
      try {
        const response = await TilopayFetchAPI.getStatus(order);
        const data = response?.data?.data;
        if (cancelled || !data) return;
        setView(data);
        setProblem("");

        if (data.status === "registered") {
          clearCheckoutStorageAfterPayment();
          timer = setTimeout(() => {
            if (!cancelled) navigate(data.order_code ? `/order/pending/${data.order_code}` : "/orders", { replace: true });
          }, REDIRECT_DELAY_MS);
          return;
        }
        if (!PAYMENT_PENDING_STATUSES.includes(data.status)) {
          writePending(null); // resultado definitivo: ya no hay nada que retomar
          return;
        }
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 401 || status === 404) {
          setProblem("No pudimos verificar tu pago porque la sesion expiro. Inicia sesion de nuevo; si ya se te cobro, el comercio registrara tu pedido.");
          return;
        }
        setProblem("Sin conexion con el servidor. Reintentando...");
      }
      if (Date.now() - startedAt > MAX_POLL_MS) {
        setTimedOut(true);
        return;
      }
      timer = setTimeout(tick, POLL_INTERVAL_MS);
    };

    tick();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [order, round, navigate]);

  if (!order) {
    return (
      <div className="container py-5 text-center">
        <h2>No encontramos el pago</h2>
        <button type="button" className="btn btn-dark mt-3" onClick={() => navigate("/cart")}>Volver al carrito</button>
      </div>
    );
  }

  const status = view?.status || "created";
  const copy = getStatusCopy(status);
  const isPending = PAYMENT_PENDING_STATUSES.includes(status);
  const needsReview = status === "mismatch" || status === "duplicate_approved";

  return (
    <div className="container py-5 text-center" style={{ maxWidth: 560 }}>
      {isPending && !timedOut && !problem.includes("sesion") && (
        <div className="spinner-border mb-3" role="status" aria-label="Confirmando pago" />
      )}
      <h2 className="mb-3">{copy.title}</h2>
      <p className={`text-${copy.tone === "info" ? "muted" : copy.tone}`}>{copy.text}</p>

      {problem && <div className="alert alert-warning">{problem}</div>}

      {timedOut && (
        <>
          <p>Todavía no recibimos la confirmación del banco.</p>
          <button type="button" className="btn btn-dark me-2" onClick={() => setRound((n) => n + 1)}>Verificar de nuevo</button>
        </>
      )}

      {(status === "declined" || status === "abandoned") && (
        <button type="button" className="btn btn-dark" onClick={() => navigate("/cart")}>Volver al carrito</button>
      )}
      {needsReview && <p className="small text-muted">Referencia: {order}</p>}
    </div>
  );
};

export default TilopayResult;
